/** Données du navigateur rangées par licence (audit de persistance du 03/10/2026, constats M8, m6, m8).
 *
 *  Sur un PC de salle partagé entre deux licences, rien de ce qu'une licence laisse dans le navigateur ne doit
 *  être proposé à l'autre. Les clés propres à une licence (brouillons des éditeurs, dossier ouvert et état de
 *  l'interface, premiers pas de l'accueil) sont rangées sous la référence opaque de son espace, renvoyée par le
 *  serveur (`space_ref` de GET /api/licence : ni le code ni l'empreinte de l'espace) :
 *  `generex@<référence>:<clé>`.
 *
 *  - Au démarrage, la référence est celle de la dernière licence vue par ce navigateur : l'état de l'interface se
 *    relit aussitôt sous elle, avant même la réponse du serveur.
 *  - Première référence connue (navigateur neuf, ou version d'avant ce rangement) : les clés d'avant, sans
 *    préfixe, passent une fois sous la licence en cours (`adopt` → « migrated »).
 *  - Autre licence que celle du démarrage (libération puis activation d'un autre code, autre onglet) : les
 *    brouillons et l'état de l'interface de la précédente sont effacés du navigateur, puis la page est rechargée
 *    (`adopt` → « changed ») : aucune donnée de la précédente ne s'affiche, même brièvement.
 *  - Libération volontaire du poste (`release`) : les brouillons de la licence sont effacés du navigateur.
 *  - Dans les deux cas, rien ne reste sous la référence quittée (premiers pas compris) et la prise en main est
 *    remise à zéro : elle se rouvre pour la licence suivante.
 *
 *  Restent communs au navigateur, sans préfixe : la langue, le thème, le modèle d'IA, la largeur de la barre
 *  latérale, les préférences de la carte. Ce sont des choix de la personne devant ce poste et de son écran, qui ne
 *  disent rien d'une licence ni de ses dossiers ; les réinitialiser à chaque changement de licence obligerait
 *  le même officier à les refaire sans rien protéger. La trace du poste (`generex:poste`) reste aussi commune :
 *  elle sert à l'écran d'activation, avant qu'aucune licence ne soit connue.
 *
 *  Comptes nominatifs (docs/17 §4.8, §6.4) : sur une licence passée aux comptes, les mêmes clés se rangent sous la
 *  référence opaque de la personne (`scope_ref` de GET /api/auth/session) au lieu de celle de la licence. Le registre
 *  `generex:licence-scopes` dit à quelle licence appartient chaque référence de personne et quand elle a servi :
 *
 *  - première connexion d'un compte sur ce navigateur : ce qui est rangé sous la licence (brouillons du mode hérité)
 *    passe une fois sous sa référence (`adoptAccount` → « migrated ») ;
 *  - autre compte de la même licence (changement d'utilisateur) : rien n'est effacé, chacun retrouve ses saisies à
 *    son retour (« switched ») ; verrouillage, expiration et changement d'utilisateur gardent les brouillons ;
 *  - déconnexion volontaire (`signOut`) : les brouillons de la personne sont effacés de ce navigateur ;
 *  - référence de personne inutilisée depuis 7 jours : purgée ;
 *  - libération du poste (`release`) : toutes les références de la licence sont purgées. */

/** Dernière référence de licence vue par ce navigateur (commune, sans préfixe). */
export const SCOPE_KEY = "generex:licence-scope";
/** Préfixe des clés rangées sous une licence. */
export const SCOPED_PREFIX = "generex@";
/** Registre des références de personne (commun, sans préfixe) : licence et dernière utilisation de chacune. */
export const SCOPES_KEY = "generex:licence-scopes";
/** Identifiant retenu sur ce poste (« Retenir mon identifiant sur ce poste »), avec la licence à laquelle il sert. */
export const REMEMBERED_LOGIN_KEY = "generex:identifiant";
/** Référence de personne purgée après tant de jours sans usage sur ce navigateur. */
export const ACCOUNT_SCOPE_DAYS = 7;
const DAY_MS = 86_400_000;

interface ScopeEntry {
  /** Référence de la licence (`space_ref`) de la personne. */
  space: string;
  /** Dernière utilisation (ms). */
  at: number;
}

/** Clés (ou débuts de clé) propres à une licence. Toute nouvelle clé de brouillon ou d'état lié aux dossiers
 *  doit y figurer, et se lire et s'écrire par `licenceStorage` ou `licenceStore`. */
export const LICENCE_KEYS: readonly string[] = [
  "generex-ui",
  "generex-draft:",
  "generex-order-draft:",
  "generex-odb-draft:",
  "generex-incident-draft:",
  "generex-aar-draft:",
  "generex-briefing-draft:",
  "generex:premiers-pas:",
  // Dernier repère vu de l'OPO sur ce poste (paragraphes mis à jour d'après le dossier, views/order/changes.ts).
  "generex-order-seen:",
  // Encart « Votre guide de prise en main » de l'accueil vu (help/guide.tsx) : il revient avec une autre licence.
  "generex:guide-encart",
];

/** Clés d'avant le rangement rattachées à aucun dossier : le brouillon d'un dossier pas encore créé (texte des
 *  Données initiales) et l'état de l'interface. Reprises seulement si le poste était déjà ouvert au chargement de
 *  la page (`adopt`, `inherit`) : sinon rien ne dit à quelle licence elles appartenaient (PC de salle partagé), et
 *  la licence qui s'ouvre les verrait. Les brouillons liés à un dossier, eux, ne s'affichent qu'à l'ouverture de
 *  ce dossier, que seule sa licence peut ouvrir. */
const UNBOUND: readonly string[] = ["generex-draft:new", "generex-ui"];

/** Marques communes au navigateur remises à zéro quand la licence change ou que le poste est libéré : la prise en
 *  main (`help/helpStore`) s'ouvre ainsi d'elle-même pour l'officier d'une autre licence. */
const RESET_ON_CHANGE: readonly string[] = ["generex:prise-en-main"];

/** Brouillons d'un dossier : `<préfixe><dossier>`, suivi d'un « : » et du reste de la clé, ou seul. */
const DOSSIER_DRAFTS: readonly string[] = [
  "generex-draft:",
  "generex-order-draft:",
  "generex-incident-draft:",
  "generex-aar-draft:",
  "generex-briefing-draft:",
];

/** Toutes les saisies gardées (celles d'un dossier, et les ordres de bataille construits). */
const DRAFTS: readonly string[] = [...DOSSIER_DRAFTS, "generex-odb-draft:"];

export const isLicenceKey = (key: string) => LICENCE_KEYS.some((p) => (p.endsWith(":") ? key.startsWith(p) : key === p));

/** Stockage brut du navigateur (localStorage), injectable pour les essais. */
export interface RawStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
  readonly length: number;
}

/** Stockage d'une licence, clés sans préfixe (même forme que celui de la file de l'OPO). */
export interface ScopedStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
  /** Clés de la licence en cours (sans leur préfixe). */
  keys(): string[];
}

/** Résultat d'une adoption : même portée ; clés reprises sous la nouvelle (première licence, ou premier compte) ;
 *  autre licence (brouillons de la précédente effacés, page à recharger) ; autre portée de la même licence, sans
 *  rien effacer (« switched » : l'état de l'interface est à relire sous elle). */
export type AdoptResult = "same" | "migrated" | "changed" | "switched";

export class LicenceScope {
  private ref: string | null;
  /** Plus aucune écriture (page sur le point d'être rechargée pour une autre licence, ou poste libéré) : une copie
   *  faite à la fermeture de la page ne peut pas se ranger sous la licence suivante. */
  private frozen = false;

  constructor(private readonly raw: () => RawStorage | null) {
    this.ref = this.read(SCOPE_KEY);
  }

  /** Référence de la licence sous laquelle ce document range ses données (null : aucune encore). */
  current(): string | null {
    return this.ref;
  }

  /** Clé effective : rangée sous la licence en cours pour une clé propre à la licence. Avant toute licence connue,
   *  la clé d'origine (elle passera sous la première licence adoptée). */
  key(key: string): string {
    return this.ref && isLicenceKey(key) ? `${SCOPED_PREFIX}${this.ref}:${key}` : key;
  }

  /** La référence renvoyée par le serveur désigne-t-elle une autre licence que celle de ce document ? Lecture
   *  seule : le portail s'en sert pour ne rien afficher de l'ancienne pendant la bascule. */
  differs(ref: string | null | undefined): boolean {
    return Boolean(ref) && this.ref !== null && ref !== this.ref && this.spaceOf(this.ref) !== ref;
  }

  /** Licence d'une référence : celle du registre pour une personne, elle-même pour une licence. */
  spaceOf(ref: string): string {
    return this.registry()[ref]?.space ?? ref;
  }

  /** La portée en cours est-elle celle d'une personne (licence passée aux comptes) ? */
  isAccountScope(ref: string | null = this.ref): boolean {
    return ref !== null && ref in this.registry();
  }

  /** Adopte la référence du serveur : rien si c'est la même ; reprise des clés d'avant sous la première licence
   *  connue ; autre licence : brouillons de la précédente effacés (la page doit alors être rechargée).
   *  `inherit` : le poste était déjà ouvert au chargement de la page, les clés d'avant sont les siennes ; faux, celles
   *  qui ne sont rattachées à aucun dossier (`UNBOUND`) sont effacées au lieu d'être reprises. */
  adopt(ref: string | null | undefined, { inherit = true, accounts = false }: { inherit?: boolean; accounts?: boolean } = {}): AdoptResult {
    if (!ref || ref === this.ref) return "same";
    const previous = this.ref;
    // Portée d'une personne de cette même licence : gardée telle quelle si la licence est en mode comptes (le
    // portail des comptes choisit la portée) ; sinon (retour au mode hérité, repli), retour à la portée de la licence,
    // sans rien effacer.
    if (previous !== null && this.spaceOf(previous) === ref) {
      if (accounts) return "same";
      this.ref = ref;
      this.write(SCOPE_KEY, ref);
      return "switched";
    }
    const keys = this.allKeys();
    if (previous === null) {
      for (const key of keys) {
        if (!isLicenceKey(key)) continue;
        const value = this.read(key);
        this.remove(key);
        if (!inherit && UNBOUND.includes(key)) continue;
        if (value !== null && this.read(`${SCOPED_PREFIX}${ref}:${key}`) === null) this.write(`${SCOPED_PREFIX}${ref}:${key}`, value);
      }
    } else {
      this.purgeLicence(this.spaceOf(previous), keys);
      this.purgeScope(previous, keys);
      for (const key of keys) if (isLicenceKey(key)) this.remove(key); // restes d'avant le rangement
      for (const key of RESET_ON_CHANGE) this.remove(key);
    }
    this.ref = ref;
    this.write(SCOPE_KEY, ref);
    return previous === null ? "migrated" : "changed";
  }

  /** Adopte la portée d'une personne (`scope`, `scope_ref` du serveur) de la licence `space` (docs/17 §4.8, §6.4).
   *  `drafts` : saisies reprises de la portée de la licence (avis « Saisies non enregistrées de ce poste reprises »).
   *  Les références de personne inutilisées depuis 7 jours sont purgées au passage. */
  adoptAccount(scope: string | null | undefined, space: string | null | undefined, now = Date.now()): { result: AdoptResult; drafts: number } {
    if (!scope || !space) return { result: "same", drafts: 0 };
    const previous = this.ref;
    let result: AdoptResult;
    let drafts = 0;
    if (previous === scope) result = "same";
    else if (previous !== null && previous !== space && this.spaceOf(previous) !== space) {
      // Autre licence (le portail de licence l'a normalement déjà traitée) : rien d'elle ne reste.
      const keys = this.allKeys();
      this.purgeLicence(this.spaceOf(previous), keys);
      this.purgeScope(previous, keys);
      for (const key of keys) if (isLicenceKey(key)) this.remove(key);
      for (const key of RESET_ON_CHANGE) this.remove(key);
      result = "changed";
    } else if (previous === null || previous === space) {
      // Première connexion de la personne sur ce navigateur après le mode hérité : ce qui était rangé sous la licence
      // (ou, avant tout rangement, sans préfixe) passe une fois sous sa référence. Sans préfixe, seules les clés liées
      // à un dossier sont reprises (rien ne dit à qui appartenaient les autres).
      const from = previous === null ? "" : `${SCOPED_PREFIX}${previous}:`;
      for (const raw of this.allKeys()) {
        if (from ? !raw.startsWith(from) : raw.startsWith(SCOPED_PREFIX)) continue;
        const key = raw.slice(from.length);
        if (!isLicenceKey(key)) continue;
        const value = this.read(raw);
        this.remove(raw);
        if (!from && UNBOUND.includes(key)) continue;
        const target = `${SCOPED_PREFIX}${scope}:${key}`;
        if (value !== null && this.read(target) === null) {
          this.write(target, value);
          if (DRAFTS.some((prefix) => key.startsWith(prefix))) drafts++;
        }
      }
      result = "migrated";
    } else result = "switched"; // autre personne de la même licence : ses saisies restent sous sa référence
    this.ref = scope;
    this.write(SCOPE_KEY, scope);
    const registry = this.registry();
    registry[scope] = { space, at: now };
    // Références de personne inutilisées depuis 7 jours : purgées de ce navigateur.
    const keys = this.allKeys();
    for (const [ref, entry] of Object.entries(registry)) {
      if (ref === scope || now - entry.at <= ACCOUNT_SCOPE_DAYS * DAY_MS) continue;
      this.purgeScope(ref, keys);
      delete registry[ref];
    }
    this.saveRegistry(registry);
    return { result, drafts };
  }

  /** Changement d'utilisateur, verrouillage quitté par un rechargement : la page suivante démarre sous la portée de la
   *  licence ; les saisies de la personne restent sous sa référence (rendues à son retour). */
  leaveAccount(now = Date.now()): void {
    const ref = this.ref;
    if (!ref || !this.isAccountScope(ref)) return;
    const registry = this.registry();
    const entry = registry[ref];
    if (entry) {
      registry[ref] = { ...entry, at: now };
      this.saveRegistry(registry);
    }
    this.ref = entry?.space ?? null;
    if (this.ref) this.write(SCOPE_KEY, this.ref);
    else this.remove(SCOPE_KEY);
  }

  /** Déconnexion volontaire : les saisies de la personne quittent ce navigateur (brouillons, état de l'interface,
   *  premiers pas), la prise en main est remise à zéro, et la page suivante démarre sous la portée de la licence. */
  signOut(): void {
    const ref = this.ref;
    if (!ref || !this.isAccountScope(ref)) return;
    const registry = this.registry();
    const space = registry[ref]?.space ?? null;
    this.purgeScope(ref, this.allKeys());
    delete registry[ref];
    this.saveRegistry(registry);
    for (const key of RESET_ON_CHANGE) this.remove(key);
    this.ref = space;
    if (space) this.write(SCOPE_KEY, space);
    else this.remove(SCOPE_KEY);
  }

  /** Poste libéré volontairement : les brouillons et l'état de l'interface de la licence quittent le navigateur, et
   *  aucune licence n'y est plus rattachée (la prochaine activation part de zéro). */
  release(): void {
    const keys = this.allKeys();
    if (this.ref) {
      const space = this.spaceOf(this.ref);
      this.purgeLicence(space, keys);
      this.purgeScope(space, keys);
      this.purgeScope(this.ref, keys);
      if (this.rememberedFor(space)) this.remove(REMEMBERED_LOGIN_KEY);
    }
    for (const key of keys) if (isLicenceKey(key)) this.remove(key);
    for (const key of RESET_ON_CHANGE) this.remove(key);
    this.ref = null;
    this.remove(SCOPE_KEY);
  }

  /** Saisies gardées sur ce poste pour la licence en cours (brouillons et copies locales des éditeurs). */
  storedDrafts(): number {
    return this.store.keys().filter((key) => DRAFTS.some((p) => key.startsWith(p))).length;
  }

  /** Plus aucune écriture jusqu'au rechargement de la page. */
  freeze(): void {
    this.frozen = true;
  }

  /** Brouillons gardés pour un dossier supprimé (Données initiales, OPO, incident, 3A, dossier d'exercice). */
  forgetDossier(dossierId: string): void {
    if (!dossierId) return;
    for (const key of this.store.keys()) {
      if (DOSSIER_DRAFTS.some((p) => key === `${p}${dossierId}` || key.startsWith(`${p}${dossierId}:`))) this.store.remove(key);
    }
  }

  /** Stockage de la licence en cours, clés sans préfixe. */
  readonly store: ScopedStore = {
    get: (key) => this.read(this.key(key)),
    set: (key, value) => this.write(this.key(key), value),
    remove: (key) => this.remove(this.key(key)),
    keys: () => {
      const prefix = this.ref ? `${SCOPED_PREFIX}${this.ref}:` : null;
      return this.allKeys().flatMap((key) => {
        if (prefix) return key.startsWith(prefix) ? [key.slice(prefix.length)] : [];
        return key.startsWith(SCOPED_PREFIX) ? [] : [key];
      });
    },
  };

  /** Même stockage, sous la forme `Storage` (persistance de zustand, brouillon des Données initiales). */
  readonly storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> = {
    getItem: (key) => this.store.get(key),
    setItem: (key, value) => this.store.set(key, value),
    removeItem: (key) => this.store.remove(key),
  };

  /** Identifiant retenu sur ce poste pour la licence `space` (null : aucun). */
  rememberedLogin(space: string | null | undefined): string | null {
    return space ? this.rememberedFor(space) : null;
  }

  /** Retient (ou oublie, `login` null) l'identifiant de la personne pour la licence `space`. */
  rememberLogin(space: string, login: string | null): void {
    if (login) this.write(REMEMBERED_LOGIN_KEY, JSON.stringify({ space, login }));
    else if (this.rememberedFor(space) !== null) this.remove(REMEMBERED_LOGIN_KEY);
  }

  private rememberedFor(space: string): string | null {
    try {
      const value = JSON.parse(this.read(REMEMBERED_LOGIN_KEY) ?? "null") as { space?: unknown; login?: unknown } | null;
      return value && value.space === space && typeof value.login === "string" ? value.login : null;
    } catch {
      return null;
    }
  }

  private registry(): Record<string, ScopeEntry> {
    try {
      const value = JSON.parse(this.read(SCOPES_KEY) ?? "null") as unknown;
      if (!value || typeof value !== "object") return {};
      const out: Record<string, ScopeEntry> = {};
      for (const [ref, entry] of Object.entries(value as Record<string, unknown>)) {
        const { space, at } = (entry ?? {}) as Record<string, unknown>;
        if (typeof space === "string" && space) out[ref] = { space, at: typeof at === "number" ? at : 0 };
      }
      return out;
    } catch {
      return {};
    }
  }

  private saveRegistry(registry: Record<string, ScopeEntry>) {
    if (Object.keys(registry).length) this.write(SCOPES_KEY, JSON.stringify(registry));
    else this.remove(SCOPES_KEY);
  }

  /** Références de personne de la licence `space` : purgées de ce navigateur et retirées du registre. */
  private purgeLicence(space: string, keys: string[]) {
    const registry = this.registry();
    let changed = false;
    for (const [ref, entry] of Object.entries(registry)) {
      if (entry.space !== space) continue;
      this.purgeScope(ref, keys);
      delete registry[ref];
      changed = true;
    }
    if (changed) this.saveRegistry(registry);
  }

  /** Tout ce que la licence quittée a laissé dans le navigateur, simples marques d'interface comprises : aucune
   *  clé ne reste sous sa référence (elle dirait qu'elle est passée par ce poste). */
  private purgeScope(ref: string, keys: string[]) {
    const prefix = `${SCOPED_PREFIX}${ref}:`;
    for (const key of keys) if (key.startsWith(prefix)) this.remove(key);
  }

  private allKeys(): string[] {
    try {
      const raw = this.raw();
      if (!raw) return [];
      const out: string[] = [];
      for (let i = 0; i < raw.length; i++) {
        const key = raw.key(i);
        if (key) out.push(key);
      }
      return out;
    } catch {
      return [];
    }
  }

  private read(key: string): string | null {
    try {
      return this.raw()?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: string) {
    if (this.frozen) return;
    try {
      this.raw()?.setItem(key, value);
    } catch {
      /* stockage plein ou bloqué : rien n'est gardé, la saisie reste en mémoire */
    }
  }

  private remove(key: string) {
    try {
      this.raw()?.removeItem(key);
    } catch {
      /* idem */
    }
  }
}

function browserStorage(): RawStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** Rangement de ce document (localStorage du navigateur). */
export const licenceScope = new LicenceScope(browserStorage);
/** Stockage de la licence en cours, clés sans préfixe (`get`, `set`, `remove`, `keys`). */
export const licenceStore: ScopedStore = licenceScope.store;
/** Même stockage, forme `Storage`. */
export const licenceStorage = licenceScope.storage;
